import React, { useState } from 'react';
import { useWallet } from '../hooks/useWallet';
import './WalletModal.css';

export default function WalletModal({ onClose }) {
  const { connectKeplr, generateWallet, importWallet, loading, error } = useWallet();
  const [view, setView] = useState('main'); // main | generate | mnemonic | import
  const [mnemonic, setMnemonic] = useState('');
  const [importInput, setImportInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleKeplr = async () => {
    await connectKeplr();
    if (!error) onClose();
  };

  const handleGenerate = async () => {
    const generatedMnemonic = await generateWallet();
    if (generatedMnemonic) {
      setMnemonic(generatedMnemonic);
      setView('mnemonic');
    }
  };

  const handleImport = async () => {
    await importWallet(importInput);
    if (!error) onClose();
  };

  const copyMnemonic = () => {
    navigator.clipboard.writeText(mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const mnemonicWords = mnemonic.split(' ');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        {view === 'main' && (
          <>
            <div className="modal-header">
              <h2 className="modal-title">Connect Wallet</h2>
              <p className="modal-sub">Choose how you want to connect to Amitis Network</p>
            </div>

            <div className="wallet-options">
              <button className="wallet-option" onClick={handleKeplr} disabled={loading}>
                <div className="wallet-option-icon">🔑</div>
                <div className="wallet-option-info">
                  <strong>Keplr Wallet</strong>
                  <span>Connect with your existing Keplr wallet</span>
                </div>
                <span className="wallet-option-arrow">→</span>
              </button>

              <button className="wallet-option" onClick={() => setView('generate')} disabled={loading}>
                <div className="wallet-option-icon">✨</div>
                <div className="wallet-option-info">
                  <strong>Generate New Wallet</strong>
                  <span>Create a new wallet in your browser</span>
                </div>
                <span className="wallet-option-arrow">→</span>
              </button>

              <button className="wallet-option" onClick={() => setView('import')} disabled={loading}>
                <div className="wallet-option-icon">📥</div>
                <div className="wallet-option-info">
                  <strong>Import Wallet</strong>
                  <span>Import using a 24-word seed phrase</span>
                </div>
                <span className="wallet-option-arrow">→</span>
              </button>
            </div>

            {error && <div className="modal-error">{error}</div>}
          </>
        )}

        {view === 'generate' && (
          <>
            <button className="modal-back" onClick={() => setView('main')}>← Back</button>
            <div className="modal-header">
              <h2 className="modal-title">Generate New Wallet</h2>
              <p className="modal-sub">A new wallet will be created in your browser. Save your seed phrase — it's the only way to recover your funds.</p>
            </div>
            <div className="warning-box">
              <span>⚠️</span>
              <p>Your 24-word seed phrase will be shown once. Write it down and store it safely. Anyone with your seed phrase can access your funds.</p>
            </div>
            <button
              className="btn-primary"
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : 'Generate Wallet'}
            </button>
            {error && <div className="modal-error">{error}</div>}
          </>
        )}

        {view === 'mnemonic' && (
          <>
            <div className="modal-header">
              <h2 className="modal-title">Save Your Seed Phrase</h2>
              <p className="modal-sub">Write these 24 words down in order. Do not share them with anyone.</p>
            </div>
            
            <div className="mnemonic-grid">
              {mnemonicWords.map((word, i) => (
                <div key={i} className="mnemonic-word">
                  <span className="word-num">{i + 1}</span>
                  <span className="word-text">{word}</span>
                </div>
              ))}
            </div>

            <button className="btn-secondary" onClick={copyMnemonic} style={{marginBottom: '12px'}}>
              {copied ? '✓ Copied to clipboard' : '📋 Copy seed phrase'}
            </button>

            <label className="confirm-check">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
              />
              <span>I have written down my seed phrase and stored it safely</span>
            </label>

            <button
              className="btn-primary"
              onClick={onClose}
              disabled={!confirmed}
              style={{marginTop: '12px'}}
            >
              Continue to Wallet
            </button>
          </>
        )}

        {view === 'import' && (
          <>
            <button className="modal-back" onClick={() => setView('main')}>← Back</button>
            <div className="modal-header">
              <h2 className="modal-title">Import Wallet</h2>
              <p className="modal-sub">Enter your 24-word seed phrase to restore your wallet.</p>
            </div>
            <textarea
              className="mnemonic-input"
              placeholder="word1 word2 word3 ... word24"
              value={importInput}
              onChange={e => setImportInput(e.target.value)}
              rows={4}
            />
            <button
              className="btn-primary"
              onClick={handleImport}
              disabled={loading || !importInput.trim()}
            >
              {loading ? <span className="spinner" /> : 'Import Wallet'}
            </button>
            {error && <div className="modal-error">{error}</div>}
          </>
        )}

        <p className="modal-footer">
          New to Amitis? <a href="https://amitis.network" target="_blank" rel="noreferrer">Learn more →</a>
        </p>
      </div>
    </div>
  );
}
